class UpcomingMatches extends HTMLElement {
  constructor() {
    super();
    this.currentLeagueIndex = 0;
    this.transitionInterval = null;
    this.timerAnimationFrame = null;
    this.matchesData = null;
    this.collectionsData = null;
    this.leagues = [];
    this.leaguePanels = [];
    this.leagueButtons = [];
    this.timerIndicators = [];
    this.transitionDuration = 5000;
    this.autoTransitionPaused = false;
    this.pauseTimeout = null;
  }

  connectedCallback() {
    this.loadData();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  loadData() {
    const dataScript = document.getElementById('matches-data');
    if (!dataScript) {
      console.error('Matches data not found');
      return;
    }

    try {
      const data = JSON.parse(dataScript.textContent);
      this.matchesData = data.matches || [];
      this.collectionsData = data.collections || [];

      // Preload all collection images
      this.preloadImages();

      if (this.matchesData.length === 0) {
        console.warn('No matches found in data.');
        return;
      }

      this.processData();
      this.render();
      this.init();
    } catch (error) {
      console.error('Error parsing matches data:', error);
      console.log('Raw data:', dataScript.textContent);
    }
  }

  preloadImages() {
    this.collectionsData.forEach(collection => {
      if (collection.image) {
        const img = new Image();
        img.src = collection.image;
      }
    });
  }

  processData() {
    const now = Math.floor(Date.now() / 1000);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Filter matches - only include upcoming ones (exclude past matches and matches with invalid timestamps)
    const upcomingMatches = this.matchesData.filter(match => {
      const timestamp = parseInt(match.timestamp) || 0;
      // Only include matches with valid timestamps that are in the future
      return timestamp > 0 && timestamp > now;
    });

    // If no upcoming matches, hide the entire section
    if (upcomingMatches.length === 0) {
      const section = this.closest('.upcoming-matches');
      if (section) {
        section.style.display = 'none';
      }
      return;
    }

    // Group matches by league
    const leaguesMap = new Map();

    upcomingMatches.forEach(match => {
      const leagueId = match.league_id;
      const timestamp = parseInt(match.timestamp) || 0;
      
      if (!leaguesMap.has(leagueId)) {
        const collection = this.collectionsData.find(c => c.league_id === leagueId);
        leaguesMap.set(leagueId, {
          id: leagueId,
          name: match.league_name,
          matches: [],
          collection: collection || null,
          earliestTimestamp: timestamp
        });
      }

      const league = leaguesMap.get(leagueId);
      league.matches.push({
        ...match,
        matchDate: new Date(timestamp * 1000),
        matchTime: new Date(timestamp * 1000)
      });
      
      // Update earliest timestamp (only if valid)
      if (timestamp > 0 && (league.earliestTimestamp === 0 || timestamp < league.earliestTimestamp)) {
        league.earliestTimestamp = timestamp;
      }
    });

    // Convert to array, sort by earliest match, and group matches by day within each league
    this.leagues = Array.from(leaguesMap.values())
      .map(league => {
        // Group matches by day within this league
        const daysGroups = this.groupMatchesByDay(league.matches, today);
        return {
          ...league,
          daysGroups: daysGroups
        };
      })
      .sort((a, b) => a.earliestTimestamp - b.earliestTimestamp);
  }

  groupMatchesByDay(matches, today) {
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const daysMap = new Map();

    matches.forEach(match => {
      const matchDate = new Date(match.matchDate);
      matchDate.setHours(0, 0, 0, 0);

      let dayKey, dayLabel;
      const daysDiff = Math.floor((matchDate - today) / (1000 * 60 * 60 * 24));

      if (daysDiff === 0) {
        dayKey = 'today';
        dayLabel = 'اليوم';
      } else if (daysDiff === 1) {
        dayKey = 'tomorrow';
        dayLabel = 'غداً';
      } else {
        dayKey = `day_${daysDiff}`;
        dayLabel = `خلال ${daysDiff} أيام`;
      }

      if (!daysMap.has(dayKey)) {
        daysMap.set(dayKey, {
          key: dayKey,
          label: dayLabel,
          daysDiff: daysDiff,
          matches: []
        });
      }

      daysMap.get(dayKey).matches.push(match);
    });

    // Convert to array and sort by days difference
    return Array.from(daysMap.values())
      .map(day => ({
        ...day,
        matches: day.matches.sort((a, b) => a.timestamp - b.timestamp)
      }))
      .sort((a, b) => a.daysDiff - b.daysDiff);
  }

  formatTime(date) {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');
    // Use LTR mark to ensure correct order in RTL contexts
    return `\u200E${displayHours}:${displayMinutes} ${ampm}`;
  }

  render() {
    const selector = this.querySelector('[data-league-selector]');
    const content = this.querySelector('[data-content]');
    const collectionImage = this.querySelector('[data-collection-image]');
    const ctaText = this.dataset.ctaText || 'تسوق الآن';
    
    if (!selector || !content) return;

    // Render league selector buttons (using collection name)
    selector.innerHTML = this.leagues.map((league, index) => {
      const collectionName = league.collection ? league.collection.title : league.name;
      return `
        <button
          class="upcoming-matches__league-btn ${index === 0 ? 'upcoming-matches__league-btn--active' : ''}"
          data-league-id="${league.id}"
          data-league-index="${index}"
          aria-label="${collectionName}"
        >
          <div class="upcoming-matches__timer-indicator" data-timer data-league-index="${index}">
            <svg class="upcoming-matches__timer-svg" viewBox="0 0 24 24">
              <circle class="upcoming-matches__timer-circle" cx="12" cy="12" r="10" />
            </svg>
          </div>
          <span class="upcoming-matches__league-name">${collectionName}</span>
        </button>
      `;
    }).join('');

    // Render league panels
    content.innerHTML = this.leagues.map((league, index) => {
      const collectionName = league.collection ? league.collection.title : '';
      const collectionUrl = league.collection ? league.collection.url : '';
      
      return `
        <div
          class="upcoming-matches__league-panel ${index === 0 ? 'upcoming-matches__league-panel--active' : ''}"
          data-league-panel="${league.id}"
          data-league-index="${index}"
        >
          <div class="upcoming-matches__header">
            ${collectionName && collectionUrl ? `<h3 class="upcoming-matches__section-subtitle"><a href="${collectionUrl}">${collectionName}</a></h3>` : collectionName ? `<h3 class="upcoming-matches__section-subtitle">${collectionName}</h3>` : ''}
            ${collectionUrl ? `<a href="${collectionUrl}" class="upcoming-matches__cta-link">${ctaText}</a>` : ''}
          </div>
          ${league.daysGroups.map(day => `
            ${day.matches.map((match, matchIndex) => {
              const collectionUrl = league.collection ? league.collection.url : '';
              const collectionTags = league.collection && league.collection.tags ? league.collection.tags : [];
              
              // Convert team name to handle format (lowercase, hyphenated) for tag filtering
              const homeTagHandle = match.home.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
              const awayTagHandle = match.away.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
              
              // Check if tags exist in collection
              const homeTagExists = collectionTags.includes(homeTagHandle);
              const awayTagExists = collectionTags.includes(awayTagHandle);
              
              // Render home team (link or plain text)
              // Format: /collections/{collection-handle}/{tag-handle}
              const homeTeamLink = homeTagExists ? `${collectionUrl}/${homeTagHandle}` : '';
              const homeTeamElement = homeTagExists 
                ? `<a href="${homeTeamLink}" class="upcoming-matches__team upcoming-matches__team--home" data-tag-filter="${homeTagHandle}">${match.home.toUpperCase()}</a>`
                : `<span class="upcoming-matches__team upcoming-matches__team--home upcoming-matches__team--no-link">${match.home.toUpperCase()}</span>`;
              
              // Render away team (link or plain text)
              // Format: /collections/{collection-handle}/{tag-handle}
              const awayTeamLink = awayTagExists ? `${collectionUrl}/${awayTagHandle}` : '';
              const awayTeamElement = awayTagExists
                ? `<a href="${awayTeamLink}" class="upcoming-matches__team upcoming-matches__team--away" data-tag-filter="${awayTagHandle}">${match.away.toUpperCase()}</a>`
                : `<span class="upcoming-matches__team upcoming-matches__team--away upcoming-matches__team--no-link">${match.away.toUpperCase()}</span>`;
              
              return `
                <div class="upcoming-matches__match" data-timestamp="${match.timestamp}">
                  ${matchIndex === 0 ? `<div class="upcoming-matches__day-title-wrapper"><span class="upcoming-matches__day-title">${day.label}</span></div>` : ''}
                  <div class="upcoming-matches__match-teams">
                    ${homeTeamElement}
                    <div class="upcoming-matches__vs-container">
                      <span class="upcoming-matches__vs">vs</span>
                      <div class="upcoming-matches__match-time">
                        ${this.formatTime(match.matchTime)}
                      </div>
                    </div>
                    ${awayTeamElement}
                  </div>
                </div>
              `;
            }).join('')}
          `).join('')}
        </div>
      `;
    }).join('');

    // Render collection image for first league
    if (collectionImage && this.leagues.length > 0 && this.leagues[0].collection && this.leagues[0].collection.image) {
      const collectionUrl = this.leagues[0].collection.url;
      collectionImage.innerHTML = `
        <a href="${collectionUrl}" class="upcoming-matches__collection-image-link">
          <img
            src="${this.leagues[0].collection.image}"
            alt="${this.leagues[0].collection.title} Collection"
            loading="eager"
            width="800"
            height="800"
          >
        </a>
      `;
    }

    // Update references
    this.leaguePanels = Array.from(this.querySelectorAll('.upcoming-matches__league-panel'));
    this.leagueButtons = Array.from(this.querySelectorAll('.upcoming-matches__league-btn'));
    this.timerIndicators = Array.from(this.querySelectorAll('.upcoming-matches__timer-indicator'));
  }

  init() {
    const section = this.closest('.upcoming-matches');
    if (section && section.dataset) {
      this.transitionDuration = parseInt(section.dataset.transitionInterval || 5) * 1000;
    } else {
      this.transitionDuration = 5000;
    }

    // Setup league button click handlers
    this.leagueButtons.forEach((btn, index) => {
      btn.addEventListener('click', () => {
        this.switchToLeague(index);
        // Pause auto-transition for 1 minute when button is clicked
        this.pauseAutoTransition(60000);
      });
    });

    // Setup team link click handlers to trigger tag filter
    this.setupTeamLinks();

    // Start auto-transition
    if (this.leaguePanels.length > 1) {
      this.startAutoTransition();
    }

    // Initialize timer indicators
    this.initTimerIndicators();
  }

  setupTeamLinks() {
    // Handle team link clicks with format: /collections/{collection-handle}/{tag-handle}
    const teamLinks = this.querySelectorAll('a.upcoming-matches__team[data-tag-filter]');
    teamLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');
        // Check if URL matches format: /collections/{handle}/{tag-handle}
        const match = href.match(/^(\/collections\/[^\/]+)\/([^\/]+)$/);
        if (match) {
          e.preventDefault();
          const collectionUrl = match[1]; // /collections/{handle}
          const tagHandle = match[2]; // {tag-handle}
          
          // Navigate to collection page with tag in path
          // The collection tag filter will detect the tag from the URL path
          window.location.href = collectionUrl + '/' + tagHandle;
        }
        // If format doesn't match, let default navigation happen
      });
    });
  }

  switchToLeague(index) {
    if (index === this.currentLeagueIndex) return;

    // Update panels
    this.leaguePanels.forEach((panel, i) => {
      if (i === index) {
        panel.classList.add('upcoming-matches__league-panel--active');
      } else {
        panel.classList.remove('upcoming-matches__league-panel--active');
      }
    });

    // Update buttons
    this.leagueButtons.forEach((btn, i) => {
      if (i === index) {
        btn.classList.add('upcoming-matches__league-btn--active');
      } else {
        btn.classList.remove('upcoming-matches__league-btn--active');
      }
    });

    // Update timer indicators - animate active, reset others
    this.timerIndicators.forEach((indicator, i) => {
      if (i === index) {
        // Animate the active indicator
        this.resetTimerIndicator(indicator);
      } else {
        // Reset inactive indicators
        const circle = indicator.querySelector('.upcoming-matches__timer-circle');
        if (circle) {
          const circumference = 2 * Math.PI * 10;
          circle.style.strokeDasharray = circumference;
          circle.style.strokeDashoffset = circumference;
        }
      }
    });

    // Update collection image
    const collectionImage = this.querySelector('[data-collection-image]');
    if (collectionImage && this.leagues[index] && this.leagues[index].collection && this.leagues[index].collection.image) {
      const collectionUrl = this.leagues[index].collection.url;
      collectionImage.innerHTML = `
        <a href="${collectionUrl}" class="upcoming-matches__collection-image-link">
          <img
            src="${this.leagues[index].collection.image}"
            alt="${this.leagues[index].collection.title} Collection"
            loading="eager"
            width="800"
            height="800"
          >
        </a>
      `;
    }

    this.currentLeagueIndex = index;
    this.resetAutoTransition();
  }

  startAutoTransition() {
    this.resetAutoTransition();
  }

  pauseAutoTransition(duration) {
    // Clear any existing pause timeout
    if (this.pauseTimeout) {
      clearTimeout(this.pauseTimeout);
    }

    // Stop current transition
    if (this.transitionInterval) {
      clearInterval(this.transitionInterval);
      this.transitionInterval = null;
    }

    this.autoTransitionPaused = true;

    // Hide all timer indicators
    this.timerIndicators.forEach(indicator => {
      if (indicator) {
        indicator.style.display = 'none';
      }
    });

    // Resume after duration
    this.pauseTimeout = setTimeout(() => {
      this.autoTransitionPaused = false;
      // Show timer indicators again
      this.timerIndicators.forEach((indicator, i) => {
        if (indicator) {
          indicator.style.display = 'block';
          // Reset the active indicator
          if (i === this.currentLeagueIndex) {
            this.resetTimerIndicator(indicator);
          }
        }
      });
      if (this.leaguePanels.length > 1) {
        this.startAutoTransition();
      }
    }, duration);
  }

  resetAutoTransition() {
    // Don't reset if paused
    if (this.autoTransitionPaused) {
      return;
    }

    if (this.transitionInterval) {
      clearInterval(this.transitionInterval);
    }

    // Reset timer indicator for current league
    const currentIndicator = this.timerIndicators[this.currentLeagueIndex];
    if (currentIndicator) {
      this.resetTimerIndicator(currentIndicator);
    }

    this.transitionInterval = setInterval(() => {
      if (!this.autoTransitionPaused) {
        const nextIndex = (this.currentLeagueIndex + 1) % this.leaguePanels.length;
        this.switchToLeague(nextIndex);
      }
    }, this.transitionDuration);
  }

  initTimerIndicators() {
    this.timerIndicators.forEach((indicator, index) => {
      if (index === this.currentLeagueIndex) {
        this.resetTimerIndicator(indicator);
      }
    });
  }

  resetTimerIndicator(indicator) {
    if (!indicator) return;
    
    const circle = indicator.querySelector('.upcoming-matches__timer-circle');
    if (!circle) return;

    const circumference = 2 * Math.PI * 10; // radius is 10
    circle.style.strokeDasharray = circumference;
    circle.style.strokeDashoffset = circumference;

    // Cancel any existing animation
    if (this.timerAnimationFrame) {
      cancelAnimationFrame(this.timerAnimationFrame);
    }

    // Animate countdown
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / this.transitionDuration, 1);
      const offset = circumference * (1 - progress);
      
      circle.style.strokeDashoffset = offset;

      if (progress < 1) {
        this.timerAnimationFrame = requestAnimationFrame(animate);
      } else {
        this.timerAnimationFrame = null;
      }
    };

    this.timerAnimationFrame = requestAnimationFrame(animate);
  }

  cleanup() {
    if (this.transitionInterval) {
      clearInterval(this.transitionInterval);
    }
    if (this.timerAnimationFrame) {
      cancelAnimationFrame(this.timerAnimationFrame);
    }
  }
}

customElements.define('upcoming-matches-component', UpcomingMatches);
